/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { PCSettings } from '../entities';
import { PCState } from '../enums';
import { IParentalControlService } from '../interfaces';
import { Locale, LocaleKeys } from '../locale';
import { Log } from '../logging/Log';
import { Storage } from '../storage';
import {
    TExtendedRatingLimits,
    TExtendedRatingType,
    TInternalRating,
    TMetadata,
    TPCRating,
    TPCRatingsData,
    TPCServiceParams,
    TServicesParams,
} from '../types';
// TODO: At the time of data/domain layer seperation this import needs to be fixed
import { ParentalControlsUtil } from '../utils/ParentalControlsUtil';
import { ParentalControlServiceRepositoryImpl } from './ParentalControlServiceRepositoryImpl';

export class ParentalControlServiceImpl implements IParentalControlService {
    private static readonly TAG: string = 'ParentalControlServiceImpl';

    public static readonly ALLOW_ALL_EXCEPT_ADULT_TEXT = 'ALLOW_ALL_EXCEPT_ADULT_TEXT';

    public static readonly ALLOW_ALL_TEXT = 'ALLOW_ALL_TEXT';

    public static readonly BLOCK_ALL_TEXT = 'BLOCK_ALL_TEXT';

    private static readonly VERIFIED_PIN_TIMEOUT: number =
        Storage.getInstance().getItem(Storage.Keys.PC_LOCK_TIMER) ?? 4 * 60 * 60 * 1000; // 4 hours

    private readonly allowAllLevel = Number.MAX_SAFE_INTEGER;

    private readonly blockAllLevel = -1;

    private readonly defaultBlockLevel = 0;

    private readonly mBodyId: string = '';

    private static movieRatingTypeId: string;

    private static tvRatingTypeId: string;

    private readonly movieText = 'película|movie';

    private readonly tvText = 'tv';

    private static mPCSettings: Map<string, string> = new Map();

    private readonly mRatingsInstructionsData: TExtendedRatingLimits;

    private readonly oParentalControlServiceRepositoryImpl: ParentalControlServiceRepositoryImpl;

    private static mRatingSystem: Array<{ [key: string]: string }> = [];

    private static mRatingsData: TPCRatingsData[] = [];

    private static mOptionList: Array<{
        [key: string]: TPCRating[];
    }> = [];

    private static mRatingOptions: Array<{
        [key: string]: TPCRating[];
    }> = [];

    public constructor(params: TServicesParams & TPCServiceParams) {
        this.mBodyId = params.bodyId;
        this.mRatingsInstructionsData = params.ratingsInstructionsData;
        this.oParentalControlServiceRepositoryImpl = new ParentalControlServiceRepositoryImpl(
            params,
        );
    }

    public checkPCViolations(metadata: TMetadata): boolean {
        const { internalRatings, isAdult, contentTypeId } = this.parseMedadata(metadata);

        return this.checkPCViolationsWithRatings(internalRatings, isAdult, contentTypeId);
    }

    public checkPCViolationsWithRatings(
        internalRatings: TInternalRating[],
        isAdult: boolean,
        contentType: string,
    ): boolean {
        if (ParentalControlServiceImpl.isPinRecentlyVerified()) return false;

        const internalRating = this.getMatchedRating(internalRatings);

        let contentTypeId = ParentalControlServiceImpl.tvRatingTypeId;
        if (contentType?.toLocaleLowerCase().search('movie') !== -1) {
            contentTypeId = ParentalControlServiceImpl.movieRatingTypeId;
        }

        if (this.shouldBlockAdultContent(isAdult)) {
            return true;
        }

        if (internalRating.length === 0) {
            return this.handleUnratedContent(contentTypeId);
        }

        if (internalRating.length > 1) {
            return internalRating.some((rating) =>
                this.checkRatingViolations(rating as Required<TInternalRating>),
            );
        }

        return this.checkRatingViolations(internalRating[0] as Required<TInternalRating>);
    }

    public checkRatingViolations(internalRatings: Required<TInternalRating>): boolean {
        const highestAllowedLevel = this.getPCHighestAllowedLevel(internalRatings.typeId);
        const level = this.getPCRatingLevel(internalRatings.typeId, internalRatings.valueId);
        return typeof level === 'number' && level > highestAllowedLevel;
    }

    private getPCHighestAllowedLevel(ratingTypeId: string): number {
        const highestAllowedRatings = this.getPCHighestAllowedRatings();
        const ratingIndex = Number(
            Object.values(
                Object.values(
                    highestAllowedRatings.filter(
                        (rating) => Object.keys(rating)[0] === ratingTypeId,
                    ),
                )[0],
            )[0],
        );

        const ratingOptions = ParentalControlServiceImpl.mRatingOptions.find(
            (option) => Object.keys(option)[0] === ratingTypeId,
        );

        if (
            !ratingOptions ||
            ratingIndex < 0 ||
            ratingIndex >= ratingOptions[ratingTypeId].length
        ) {
            return this.blockAllLevel;
        }

        const ratingValueId = Object.keys(ratingOptions[ratingTypeId][ratingIndex])[0];

        switch (ratingValueId) {
            case ParentalControlServiceImpl.BLOCK_ALL_TEXT:
                return this.blockAllLevel;

            case ParentalControlServiceImpl.ALLOW_ALL_TEXT:
                return this.allowAllLevel;

            case ParentalControlServiceImpl.ALLOW_ALL_EXCEPT_ADULT_TEXT:
                return this.getPCAdultLevel(ratingTypeId) - 1;

            default: {
                // Level from actual rating.
                return this.getPCRatingLevel(ratingTypeId, ratingValueId) ?? this.blockAllLevel;
            }
        }
    }

    public getPCHighestAllowedRatings(): Array<{ [key: string]: string }> {
        try {
            return JSON.parse(this.getPCSetting(PCSettings.HEIGHEST_ALLOWED_RATINGS));
        } catch (error) {
            Log.e(ParentalControlServiceImpl.TAG, 'getPCHighestAllowedRatings', error);
            return [];
        }
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCSetting(key: string): string {
        const pcSetting: string | undefined = ParentalControlServiceImpl.mPCSettings.get(key);
        if (pcSetting) return pcSetting;

        throw new Error(`PC setting for ${key} not found.`);
    }

    private handleUnratedContent(contentTypeId: string): boolean {
        const unratedContentAllowed = this.getPCUnratedContentAllowed();
        if (unratedContentAllowed.length === 1)
            return Object.values(unratedContentAllowed[0])[0] !== '1';

        const ratingVal = unratedContentAllowed.find(
            (rating) => Object.keys(rating)[0] === contentTypeId,
        );
        if (!ratingVal) return false;

        return ratingVal[contentTypeId] !== '1';
    }

    /**
     * @inheritdoc
     */
    public isPCHideAdultContentEnabled(): boolean {
        try {
            return this.getPCSetting(PCSettings.HIDE_ADULT) === 'true';
        } catch (error) {
            return true;
        }
    }

    public static isPinRecentlyVerified(): boolean {
        const verifiedTimestamp = ParentalControlServiceImpl.mPCSettings.get(
            PCSettings.VERIFIED_PIN_TIMESTAMP,
        );
        if (!verifiedTimestamp) {
            Log.e(ParentalControlServiceImpl.TAG, 'isPinRecentlyVerified - Key not found.');
            return false;
        }

        const verifiedTimestampInMilliSeconds = Number(verifiedTimestamp);

        if (Number.isNaN(verifiedTimestampInMilliSeconds)) {
            Log.e(ParentalControlServiceImpl.TAG, 'isPinRecentlyVerified - Invalid timestamp.');
            return false;
        }

        const verifiedPinTimestamp = new Date(verifiedTimestampInMilliSeconds);
        const currentTimestamp = new Date();
        const isPinAlreadyVerified: boolean =
            currentTimestamp.getTime() - verifiedPinTimestamp.getTime() <=
            ParentalControlServiceImpl.VERIFIED_PIN_TIMEOUT;
        return isPinAlreadyVerified;
    }

    /**
     * @inheritdoc
     */
    public loadPCSettings(): void {
        try {
            const pcSettingFromStorage = Storage.getInstance().getItem(Storage.Keys.PC_SETTING);
            if (pcSettingFromStorage) {
                ParentalControlServiceImpl.mPCSettings = new Map(JSON.parse(pcSettingFromStorage));
            }

            const defaultSettingsChangedValue = ParentalControlServiceImpl.mPCSettings.get(
                PCSettings.DEFAULT_SETTINGS_CHANGED,
            );
            const hasDefaultSettingsChanged: boolean =
                ParentalControlServiceImpl.mPCSettings.size &&
                defaultSettingsChangedValue &&
                JSON.parse(defaultSettingsChangedValue);
            if (!hasDefaultSettingsChanged) {
                ParentalControlServiceImpl.mPCSettings = new Map();
                this.oParentalControlServiceRepositoryImpl
                    .fetchPCSetting()
                    .forEach((value, key) => {
                        ParentalControlServiceImpl.mPCSettings.set(key, value);
                    });
                ParentalControlServiceImpl.mPCSettings.set(
                    PCSettings.PC_STATUS,
                    String(PCState.Locked),
                );
                ParentalControlServiceImpl.mPCSettings.set(
                    PCSettings.DEFAULT_SETTINGS_CHANGED,
                    'false',
                );
                ParentalControlServiceImpl.mPCSettings.set(PCSettings.UNRATED_TV_RATING, '0');
                ParentalControlServiceImpl.mPCSettings.set(PCSettings.UNRATED_MOVIE_RATING, '0');
            } else {
                ParentalControlServiceImpl.mPCSettings.set(
                    PCSettings.RATINGS_INSTRUCTIONS,
                    JSON.stringify(this.mRatingsInstructionsData),
                );
            }

            const internalRatingsData = ParentalControlServiceImpl.mPCSettings.get(
                PCSettings.RATINGS_INSTRUCTIONS,
            );

            if (internalRatingsData) {
                const mInternalRatingsData: TExtendedRatingLimits = JSON.parse(internalRatingsData);
                this.processInternalRatingsData(mInternalRatingsData);
                const ratingLimitsString = ParentalControlServiceImpl.mPCSettings.get(
                    PCSettings.RATING_LIMITS,
                );
                const ratingLimits: { [key: string]: string }[] = ratingLimitsString
                    ? JSON.parse(ratingLimitsString)
                    : [];

                if (ratingLimits.length > 0 && !hasDefaultSettingsChanged) {
                    const lowestAllowedRatingsMap: {
                        [key: string]: { index: number; level: number };
                    } = {};

                    ratingLimits.forEach((ratingType) => {
                        const { ratingTypeId, ratingValueId } = ratingType;
                        if (lowestAllowedRatingsMap[ratingTypeId] === undefined)
                            lowestAllowedRatingsMap[ratingTypeId] = {
                                index: Number.MAX_SAFE_INTEGER,
                                level: Number.MAX_SAFE_INTEGER,
                            };
                        const ratingOptions = ParentalControlServiceImpl.mRatingOptions.find(
                            (option) => Object.keys(option)[0] === ratingTypeId,
                        );

                        if (!ratingOptions) {
                            return;
                        }

                        const ratingIndex = ratingOptions[ratingTypeId].findIndex(
                            (rating) => Object.keys(rating)[0] === ratingValueId,
                        );

                        if (ratingIndex === -1) {
                            lowestAllowedRatingsMap[ratingTypeId].index = 1;
                        } else {
                            const ratinglevel =
                                ratingOptions[ratingTypeId][ratingIndex][ratingValueId].level;
                            if (ratinglevel < lowestAllowedRatingsMap[ratingTypeId].level) {
                                lowestAllowedRatingsMap[ratingTypeId].index = ratingIndex;
                                lowestAllowedRatingsMap[ratingTypeId].level = ratinglevel;
                            }
                        }
                    });

                    const optionLengths = ParentalControlServiceImpl.mRatingOptions.map(
                        (option) => Object.values(option)[0].length,
                    );

                    const heighestAllowedRatings = ParentalControlServiceImpl.mRatingSystem.map(
                        (rating, idx) => {
                            return {
                                [Object.keys(rating)[0]]: `
                                    ${
                                        lowestAllowedRatingsMap[Object.keys(rating)[0]].index === 1
                                            ? lowestAllowedRatingsMap[Object.keys(rating)[0]].index
                                            : (lowestAllowedRatingsMap[Object.keys(rating)[0]]
                                                  .index +
                                                  1) %
                                              optionLengths[idx]
                                    }`,
                            };
                        },
                    );

                    ParentalControlServiceImpl.mPCSettings.set(
                        PCSettings.HEIGHEST_ALLOWED_RATINGS,
                        JSON.stringify(heighestAllowedRatings),
                    );
                }
            }

            // reset the verfied pin timestamp
            ParentalControlServiceImpl.mPCSettings.set(PCSettings.VERIFIED_PIN_TIMESTAMP, '0');

            // store the PC settings to localStorage
            Storage.getInstance().setItem(
                Storage.Keys.PC_SETTING,
                JSON.stringify(Array.from(ParentalControlServiceImpl.mPCSettings)),
            );
        } catch (error) {
            Log.e(ParentalControlServiceImpl.TAG, 'loadPCSettings', error);
            throw error;
        }
    }

    // eslint-disable-next-line class-methods-use-this
    private parseMedadata(metadata: TMetadata): {
        internalRatings: TInternalRating[];
        isAdult: boolean;
        contentTypeId: string;
    } {
        const { airingOffers, offerPreview } = metadata;

        const isAdult =
            airingOffers?.[0]?.parentalControls?.isAdult ||
            !!offerPreview?.contentPreview?.isAdult ||
            false;

        let contentTypeId = ParentalControlServiceImpl.tvRatingTypeId;
        if (
            (airingOffers?.[0]?.banner?.programType || offerPreview?.contentPreview?.previewType)
                ?.toLocaleLowerCase()
                .search('movie') !== -1
        ) {
            contentTypeId = ParentalControlServiceImpl.movieRatingTypeId;
        }

        const internalRatings: TInternalRating[] =
            airingOffers?.[0]?.parentalControls?.rating ||
            offerPreview?.contentPreview?.internalRatingList ||
            [];

        return { internalRatings, isAdult, contentTypeId };
    }

    /**
     * @inheritdoc
     */
    public getMatchedRating(internalRatings: TInternalRating[]): TInternalRating[] {
        try {
            const { ratingValue } = JSON.parse(this.getPCSetting(PCSettings.RATINGS_INSTRUCTIONS));

            const matchedRatings = ratingValue.filter(({ ratingTypeId, ratingValueId }) => {
                return internalRatings.some(({ typeId, valueId }) => {
                    return typeId === ratingTypeId && valueId === ratingValueId;
                });
            });

            return matchedRatings.map(({ ratingTypeId, ratingValueId }) => ({
                typeId: ratingTypeId,
                valueId: ratingValueId,
            }));
        } catch (error) {
            Log.e(ParentalControlServiceImpl.TAG, 'getMatchedRating', error);
            return [];
        }
    }

    private processInternalRatingsData(internalRatingsData: TExtendedRatingLimits) {
        this.storeRatingTypeIds(internalRatingsData.ratingType);
        // create rating system based on distinct rating types and their
        // display rank in ascending order
        ParentalControlServiceImpl.mRatingSystem = [
            ...new Set(
                internalRatingsData.ratingType.map((type) => {
                    return { [type.ratingTypeId]: type.displayRank };
                }),
            ),
        ].sort((type1, type2) => Number(Object.values(type1)[0]) - Number(Object.values(type2)[0]));

        // populate all ratings data based on rating system
        ParentalControlServiceImpl.mRatingSystem.forEach((ratingType) => {
            const ratingTypeId = Object.keys(ratingType)[0];
            const ratingsValue = internalRatingsData.ratingValue.filter(
                (value) => value.ratingTypeId === ratingTypeId,
            );
            const ratingsType = internalRatingsData.ratingType.filter((type) => {
                return type.ratingTypeId === ratingTypeId;
            })[0];
            // formate ratings data in this formate
            // {ratingName: {ratingLevel,description,adultLevel}}
            const formattedRatings: TPCRating[] = ratingsValue.map((rating) => {
                return {
                    [rating.ratingValueId]: {
                        level: Number(rating.level),
                        description: rating.description,
                        ratingValueName: rating.name,
                    },
                };
            });

            formattedRatings.sort((rating1, rating2) => {
                return (
                    Number(Object.values(rating2)[0].level) -
                    Number(Object.values(rating1)[0].level)
                );
            });
            // filter ratings which are less than adult level
            const ratingsWithHACOn: TPCRating[] = formattedRatings.filter((rating) => {
                return (
                    Number(Object.values(rating)[0].level) <
                    Number(
                        internalRatingsData.ratingType.filter(
                            (type) => type.ratingTypeId === ratingTypeId,
                        )[0].adultLevel,
                    )
                );
            });
            // add data into mRatingsData based on rating type id and for each rating type
            // id store the ratings data in this formate {ratingName: {ratingLevel,description}}
            ParentalControlServiceImpl.mRatingsData.push({
                [ratingTypeId]: {
                    name: internalRatingsData.ratingType.filter(
                        (type) => type.ratingTypeId === ratingTypeId,
                    )[0].name,
                    adultLevel: Number(
                        internalRatingsData.ratingType.filter(
                            (type) => type.ratingTypeId === ratingTypeId,
                        )[0].adultLevel,
                    ),
                    blockedUnratedContent: ratingsType.blockUnratedPrograms,
                    ratingsWithHACOff: formattedRatings,
                    ratingsWithHACOn,
                },
            });
        });
        const defaultSettingsChangedValue = ParentalControlServiceImpl.mPCSettings.get(
            PCSettings.DEFAULT_SETTINGS_CHANGED,
        );
        const hasDefaultSettingsChanged: boolean =
            ParentalControlServiceImpl.mPCSettings.size &&
            defaultSettingsChangedValue &&
            JSON.parse(defaultSettingsChangedValue);
        const heighestAllowedRatings = ParentalControlServiceImpl.mRatingsData.map((ratingData) => {
            return { [Object.keys(ratingData)[0]]: '1' };
        });
        const unratedContentAllowed = ParentalControlServiceImpl.mRatingsData.map((ratingData) => {
            return {
                [Object.keys(ratingData)[0]]:
                    Object.values(ratingData)[0].blockedUnratedContent === 'true' ? '0' : '1',
            };
        });
        if (!hasDefaultSettingsChanged) {
            ParentalControlServiceImpl.mPCSettings.set(
                PCSettings.HEIGHEST_ALLOWED_RATINGS,
                JSON.stringify(heighestAllowedRatings),
            );
            ParentalControlServiceImpl.mPCSettings.set(
                PCSettings.UNRATED_CONTENT_ALLOWED,
                JSON.stringify(unratedContentAllowed),
            );
        }
        this.createOptionsList();
    }

    /**
     * @inheritdoc
     */
    public createOptionsList() {
        const optionsList = ParentalControlServiceImpl.mRatingSystem.map((ratingType) => {
            const ratingTypeId = Object.keys(ratingType)[0];
            const ratingData = ParentalControlServiceImpl.mRatingsData.filter((data) => {
                return Object.keys(data)[0] === ratingTypeId;
            })[0];
            const ratingDataValue = Object.values(ratingData)[0];
            const options = this.isPCHideAdultContentEnabled()
                ? ratingDataValue.ratingsWithHACOn
                : ratingDataValue.ratingsWithHACOff;
            return { [ratingTypeId]: options };
        });
        ParentalControlServiceImpl.mOptionList = optionsList;
        this.createPCRatingsOptions();
    }

    private shouldBlockAdultContent(isAdult: boolean): boolean {
        // Block adult content when 'Hide adult content' is on, regardless of the ratings.
        if (isAdult) {
            return this.isPCHideAdultContentEnabled();
        }
        return false;
    }

    private storeRatingTypeIds(ratingTypes: TExtendedRatingType[]) {
        ratingTypes.forEach((ratingType) => {
            const isMovieRating = this.movieText.split('|').some((text) => {
                return ratingType.name.toLowerCase().includes(text);
            });
            const isTVRating = ratingType.name.toLowerCase().includes(this.tvText);
            if (isMovieRating) {
                ParentalControlServiceImpl.movieRatingTypeId = ratingType.ratingTypeId;
            }
            if (isTVRating) {
                ParentalControlServiceImpl.tvRatingTypeId = ratingType.ratingTypeId;
            }
        });
    }

    /**
     * @inheritdoc
     */
    public updatePCSetting(key: string, value: string): void {
        try {
            ParentalControlServiceImpl.mPCSettings.set(key, value);
            ParentalControlServiceImpl.mPCSettings.set(PCSettings.DEFAULT_SETTINGS_CHANGED, 'true');
            Storage.getInstance().setItem(
                Storage.Keys.PC_SETTING,
                JSON.stringify(Array.from(ParentalControlServiceImpl.mPCSettings)),
            );
            this.oParentalControlServiceRepositoryImpl.updatePCSetting(key, value);
        } catch (error) {
            Log.e(ParentalControlServiceImpl.TAG, 'updatePCSetting', error);
            throw error;
        }
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line no-unused-vars, class-methods-use-this
    public verifyPCPin(pin: string): boolean {
        const storedPCPin = ParentalControlServiceImpl.mPCSettings.get(PCSettings.DEFAULT_PC_PIN);
        const hashGeneratedPin = ParentalControlsUtil.getHashedPassword(this.mBodyId);
        return storedPCPin === pin || hashGeneratedPin === pin;
    }

    /**
     * @inheritdoc
     */
    public getPCAdultLevel(ratingTypeId: string): number {
        try {
            const internalRatingsData: string = this.getPCSetting(PCSettings.RATINGS_INSTRUCTIONS);
            const mInternalRatingsData = JSON.parse(internalRatingsData);
            const ratingType = mInternalRatingsData.ratingType.find(
                (type) => type.ratingTypeId === ratingTypeId,
            );

            return ratingType?.adultLevel ?? this.defaultBlockLevel;
        } catch (error) {
            Log.e(ParentalControlServiceImpl.TAG, 'getPCAdultLevel', error);
            return this.defaultBlockLevel;
        }
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCRatingLevel(ratingTypeId: string, ratingValueId: string): number | undefined {
        const ratingData = ParentalControlServiceImpl.mRatingsData.find(
            (data) => Object.keys(data)[0] === ratingTypeId,
        );

        if (!ratingData) return undefined;

        const ratingLevel = Object.values(ratingData)[0].ratingsWithHACOff.find((rating) => {
            return Object.keys(rating)[0] === ratingValueId;
        });

        if (!ratingLevel) return undefined;
        return ratingLevel[ratingValueId].level;
    }

    /**
     * @inheritdoc
     */
    public getPCUnratedContentAllowed(): Array<{ [key: string]: string }> {
        try {
            return JSON.parse(this.getPCSetting(PCSettings.UNRATED_CONTENT_ALLOWED));
        } catch (error) {
            Log.e(ParentalControlServiceImpl.TAG, 'isPCUnratedContentAllowed', error);
            return [];
        }
    }

    /**
     * @inheritdoc
     */
    public createPCRatingsOptions(): void {
        const allowAllExceptAdultText = `${Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_EXCEPT_TEXT)} ${Locale.getString(LocaleKeys.SETTINGS_PC_ADULT_TEXT)}`;
        const allowAllText = Locale.getString(LocaleKeys.SETTINGS_PC_ALLOW_ALL_TEXT);
        const blockAllText = Locale.getString(LocaleKeys.SETTINGS_PC_BLOCK_ALL_TEXT);
        const ratingsOptions = ParentalControlServiceImpl.mOptionList;
        const hideAdultContent: boolean = this.isPCHideAdultContentEnabled();

        const updatedRatingsOptions = ratingsOptions.map((ratingOption) => {
            const blockAllRating = {
                [ParentalControlServiceImpl.BLOCK_ALL_TEXT]: {
                    level: this.blockAllLevel,
                    description: Locale.getString(
                        LocaleKeys.SETTINGS_PC_HIGHEST_ALLOWED_RATING_PREVIEW_TEXT,
                    ),
                    ratingValueName: blockAllText,
                },
            };
            const allowAllRating = {
                [ParentalControlServiceImpl.ALLOW_ALL_TEXT]: {
                    level: this.allowAllLevel,
                    description: Locale.getString(
                        LocaleKeys.SETTINGS_PC_SET_RATING_LIMITS_ALLOW_ALL_PREVIEW_TEXT,
                    ),
                    ratingValueName: allowAllText,
                },
            };
            const allowAllExceptAdultRating = {
                [ParentalControlServiceImpl.ALLOW_ALL_EXCEPT_ADULT_TEXT]: {
                    level: this.getPCAdultLevel(Object.keys(ratingOption)[0]) - 1,
                    description: Locale.getString(
                        LocaleKeys.SETTINGS_PC_ALLOW_ALL_EXCEPT_ADULT_PREVIEW_TEXT,
                    ),
                    ratingValueName: allowAllExceptAdultText,
                },
            };
            const updatedOptions = [
                blockAllRating,
                hideAdultContent ? allowAllExceptAdultRating : allowAllRating,
                ...Object.values(ratingOption)[0],
            ];
            return { [Object.keys(ratingOption)[0]]: updatedOptions };
        });
        const previousHighestAllowedRatings = this.getPCHighestAllowedRatings();
        let previousSelectedRatingsName: Array<{ [key: string]: string }> = [];
        if (ParentalControlServiceImpl.mRatingOptions)
            previousSelectedRatingsName = ParentalControlServiceImpl.mRatingOptions.map(
                (rating, index) => {
                    const selectedOptionIndex = Number(
                        Object.values(previousHighestAllowedRatings[index])[0],
                    );
                    return {
                        [Object.keys(rating)[0]]: Object.values(
                            Object.values(rating)[0][selectedOptionIndex],
                        )[0].ratingValueName,
                    };
                },
            );
        ParentalControlServiceImpl.mRatingOptions = updatedRatingsOptions;
        if (previousSelectedRatingsName.length === 0) return;
        const updatedHighestAllowedRatings: Array<{ [key: string]: string }> =
            updatedRatingsOptions.map((ratingOption, index) => {
                const selectedOptionIndex = Object.values(ratingOption)[0].findIndex(
                    (rating) =>
                        Object.values(rating)[0].ratingValueName ===
                        previousSelectedRatingsName[index][Object.keys(ratingOption)[0]],
                );
                return {
                    [Object.keys(ratingOption)[0]]:
                        selectedOptionIndex !== -1 ? String(selectedOptionIndex) : '1',
                };
            });
        this.updatePCSetting(
            PCSettings.HEIGHEST_ALLOWED_RATINGS,
            JSON.stringify(updatedHighestAllowedRatings),
        );
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCMovieRatingTypeId(): string {
        return ParentalControlServiceImpl.movieRatingTypeId;
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCTVRatingTypeId(): string {
        return ParentalControlServiceImpl.tvRatingTypeId;
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCRatingsData(): TPCRatingsData[] {
        return ParentalControlServiceImpl.mRatingsData;
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCOptionList(): Array<{
        [key: string]: TPCRating[];
    }> {
        return ParentalControlServiceImpl.mOptionList;
    }

    /**
     * @inheritdoc
     */
    // eslint-disable-next-line class-methods-use-this
    public getPCRatingOptions(): Array<{
        [key: string]: TPCRating[];
    }> {
        return ParentalControlServiceImpl.mRatingOptions;
    }
}
